import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import FilmFrontLogo from "@/components/FilmFrontLogo";
import { toast } from "sonner";
import { ArrowRight, Mail, Lock, User, Loader2 } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await register({ name: name.trim(), email: email.trim(), password });
      toast.success("Welcome to Film Front");
      navigate("/app", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not create your account";
      setError(typeof detail === "string" ? detail : "Please check your details");
      toast.error(typeof detail === "string" ? detail : "Please check your details");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white ff-grain relative overflow-hidden">
      <div className="ff-orange-orb top-20 -right-40 opacity-70" />
      <div className="max-w-md mx-auto px-6 py-10 relative z-10">
        <Link to="/" className="inline-block mb-14">
          <FilmFrontLogo size="md" />
        </Link>
        <div className="text-xs uppercase tracking-[0.3em] text-neutral-400 mb-3">Create your account</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter leading-none font-medium mb-8">Join the crew.</h1>
        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-neutral-400">Full name</span>
            <div className="mt-1.5 flex items-center gap-2 bg-black border border-white/10 rounded-lg px-3 py-2.5 focus-within:border-[hsl(var(--ff-accent))] transition-colors">
              <User className="w-4 h-4 text-neutral-500" />
              <input
                type="text" required autoComplete="name"
                value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Ada Directorson"
                className="bg-transparent flex-1 outline-none text-sm placeholder:text-neutral-600"
                data-testid="register-name-input"
              />
            </div>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-neutral-400">Email</span>
            <div className="mt-1.5 flex items-center gap-2 bg-black border border-white/10 rounded-lg px-3 py-2.5 focus-within:border-[hsl(var(--ff-accent))] transition-colors">
              <Mail className="w-4 h-4 text-neutral-500" />
              <input
                type="email" required autoComplete="email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@studio.com"
                className="bg-transparent flex-1 outline-none text-sm placeholder:text-neutral-600"
                data-testid="register-email-input"
              />
            </div>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-neutral-400">Password</span>
            <div className="mt-1.5 flex items-center gap-2 bg-black border border-white/10 rounded-lg px-3 py-2.5 focus-within:border-[hsl(var(--ff-accent))] transition-colors">
              <Lock className="w-4 h-4 text-neutral-500" />
              <input
                type="password" required autoComplete="new-password" minLength={6}
                value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                className="bg-transparent flex-1 outline-none text-sm placeholder:text-neutral-600"
                data-testid="register-password-input"
              />
            </div>
          </label>
          {error && <div className="text-sm text-red-400" data-testid="register-error">{error}</div>}
          <div className="text-xs text-neutral-500 leading-relaxed">
            By joining, you agree to Film Front's Community Guidelines. Members are professional filmmakers, crew, and industry supporters working in Southern Utah.
          </div>
          <button
            type="submit" disabled={loading}
            className="ff-btn-primary w-full mt-4 disabled:opacity-60"
            data-testid="register-submit-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Create account <ArrowRight className="w-4 h-4 ml-2" /></>}
          </button>
        </form>
        <div className="mt-8 text-sm text-neutral-400">
          Already a member?{" "}
          <Link to="/login" className="ff-accent-text hover:underline" data-testid="register-to-login-link">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
